// Access levels are data, not code (PRD §7.2, SEC2–SEC3, SEC15). A level is a set of
// capabilities from a fixed catalog plus a data scope. Admins edit levels and point Entra groups
// at them; the token's groups claim decides who holds what. Adding a person is an Entra group
// change; adding a level is configuration.

export const CAPABILITIES = [
  "self.read",
  "plans.read",
  "plans.edit",
  "financials.read",
  "comp.read",
  "people.edit",
  "period.close",
  "tax.admin",
  "reports.tax",
  "revenue_plan.edit",
  "subjects.read",
  "export",
  "access.admin",
  // Meeting Ops in Base Camp (PRD rev. 1.19).
  "aliases.read",
  "aliases.edit",
  "meetings.read",
  "actions.edit",
  "actions.self",
  // Expense reports (PRD rev. 1.21).
  "expenses.self",
  "expenses.approve",
  "expenses.read",
  // QuickBooks and the financial reports (PRD rev. 1.22): the Admin level only (SEC20).
  "books.read",
] as const;
export type Capability = (typeof CAPABILITIES)[number];

/** Widest first. A person in several groups gets the widest scope among them. */
export const SCOPES = ["all", "own-projects", "own-rows"] as const;
export type DataScope = (typeof SCOPES)[number];

export interface AccessLevel {
  id: string;
  name: string;
  capabilities: Capability[];
  scope: DataScope;
}

export interface GroupGrant {
  /** Entra group object id, as it appears in the token's groups claim. */
  groupId: string;
  levelId: string;
  /** What admins call the group, for the Access screen; the id is what grants access. */
  label?: string;
}

/** What each capability grants, as the Access screen explains it (PRD §7.2's catalog). */
export const CAPABILITY_TEXT: Readonly<Record<Capability, string>> = {
  "self.read": "Own assignments, hours left and utilization",
  "plans.read": "Project plans, schedules and capacity views",
  "plans.edit": "Create and edit projects, plans, templates and baselines",
  "financials.read": "Project revenue, cost and margin (at standard rates unless comp.read)",
  "comp.read": "Pay inputs and per-person rates",
  "people.edit": "People, pay, cost items and bonus",
  "period.close": "Close and reopen periods",
  "tax.admin": "Payroll tax tables and R&D activity types",
  "reports.tax": "Tax and R&D reports, R&D tagging",
  "revenue_plan.edit": "Offerings, assumptions and the top-down plan",
  "subjects.read": "Calendar event subjects, in the data-quality and tagging screens",
  export: "Excel and CSV exports",
  "access.admin": "This Access screen: levels and group mappings",
  "aliases.read": "Client aliases on person and project cards, and the Aliases screen",
  "aliases.edit": "Add and change client aliases and client contacts; send disable requests",
  "meetings.read": "Meetings, their transcript status and the action board, across people",
  "actions.edit": "Move, edit, accept and reject action items, and run the review gate, across people",
  "actions.self": "Own action items, and meetings the person is responsible for or attended",
  "expenses.self": "Own expense reports and receipts (everyone with self.read has it)",
  "expenses.approve": "Approve or return others' expense reports, never one's own",
  "expenses.read": "Every expense report and receipt, payment status, and recording payment",
  "books.read": "QuickBooks: the connection, its figures, mappings and the financial reports. Admins only (SEC20)",
};

/**
 * Everyone who reports hours files expenses too (PRD ER11, rev. 1.21): self.read brings expenses.self,
 * so stored levels need no edit for it.
 */
export function withDerived(capabilities: Iterable<Capability>): Set<Capability> {
  const out = new Set(capabilities);
  if (out.has("self.read")) out.add("expenses.self");
  return out;
}

/**
 * The seeded Admin level is "All" (PRD §7.2), but stored configurations list its capabilities
 * explicitly, so a capability added to the catalog later would be missing from it until someone
 * re-saved the level. Reading a configuration through this keeps Admin at the whole catalog.
 */
export function withCurrentCatalog(config: AccessConfig): AccessConfig {
  return {
    ...config,
    levels: config.levels.map((l) => (l.id === "admin" ? { ...l, capabilities: [...CAPABILITIES] } : l)),
  };
}

export const SCOPE_TEXT: Readonly<Record<DataScope, string>> = {
  all: "All projects and people",
  "own-projects": "Projects they own",
  "own-rows": "Their own assignments and hours",
};

/** A readable id for a new level ("Auditor" → "auditor"), unique among `taken`. */
export function newLevelId(name: string, taken: ReadonlySet<string>): string {
  const base =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 36) || "level";
  let id = base;
  for (let n = 2; taken.has(id); n++) id = `${base}-${n}`;
  return id;
}

/**
 * A short fingerprint of a configuration. The Access screen sends back the one it loaded, so an edit
 * made from a stale copy is refused rather than overwriting someone else's change.
 */
export function accessVersion(config: AccessConfig): string {
  const text = JSON.stringify({ levels: config.levels, grants: config.grants });
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193);
  return (h >>> 0).toString(16).padStart(8, "0");
}

/** One line per change, for the audit log and the Access screen's history (SEC16). */
export function describeAccessChange(before: AccessConfig, after: AccessConfig): string {
  const parts: string[] = [];
  const name = (c: AccessConfig, id: string) => c.levels.find((l) => l.id === id)?.name ?? id;
  const group = (g: GroupGrant) => (g.label ? `${g.label} (${g.groupId})` : g.groupId);
  for (const l of after.levels) {
    const was = before.levels.find((x) => x.id === l.id);
    if (!was) {
      parts.push(`Added level ${l.name}: ${l.capabilities.join(", ") || "no capabilities"}; ${SCOPE_TEXT[l.scope].toLowerCase()}`);
      continue;
    }
    if (was.name !== l.name) parts.push(`Renamed level ${was.name} to ${l.name}`);
    const added = l.capabilities.filter((c) => !was.capabilities.includes(c));
    const removed = was.capabilities.filter((c) => !l.capabilities.includes(c));
    if (added.length) parts.push(`${l.name} gains ${added.join(", ")}`);
    if (removed.length) parts.push(`${l.name} loses ${removed.join(", ")}`);
    if (was.scope !== l.scope) parts.push(`${l.name} now sees ${SCOPE_TEXT[l.scope].toLowerCase()}`);
  }
  for (const l of before.levels) if (!after.levels.some((x) => x.id === l.id)) parts.push(`Removed level ${l.name}`);
  const key = (g: GroupGrant) => `${g.groupId}|${g.levelId}`;
  for (const g of after.grants) {
    const was = before.grants.find((x) => key(x) === key(g));
    if (!was) parts.push(`Mapped group ${group(g)} to ${name(after, g.levelId)}`);
    else if ((was.label ?? "") !== (g.label ?? "")) parts.push(`Labelled group ${g.groupId} "${g.label ?? ""}"`);
  }
  for (const g of before.grants) if (!after.grants.some((x) => key(x) === key(g))) parts.push(`Unmapped group ${group(g)} from ${name(before, g.levelId)}`);
  return parts.join(". ") + (parts.length ? "." : "");
}

export interface AccessConfig {
  levels: AccessLevel[];
  grants: GroupGrant[];
}

/** PRD §7.2 seeded levels. Admins can edit these and add more. */
export const SEEDED_LEVELS: readonly AccessLevel[] = [
  { id: "admin", name: "Admin", capabilities: [...CAPABILITIES], scope: "all" },
  {
    id: "controller",
    name: "Controller",
    capabilities: ["plans.read", "financials.read", "comp.read", "period.close", "tax.admin", "reports.tax", "export", "self.read", "expenses.read"],
    scope: "all",
  },
  { id: "planner", name: "Planner", capabilities: ["plans.read", "plans.edit", "financials.read", "self.read"], scope: "own-projects" },
  { id: "viewer", name: "Viewer", capabilities: ["plans.read", "financials.read", "self.read"], scope: "all" },
  { id: "member", name: "Member", capabilities: ["self.read", "actions.self"], scope: "own-rows" },
];

export interface EffectiveAccess {
  levels: { id: string; name: string }[];
  capabilities: Set<Capability>;
  /** null when no group grants anything: the caller has no access at all. */
  scope: DataScope | null;
}

export function resolveAccess(groupIds: readonly string[], stored: AccessConfig): EffectiveAccess {
  const config = withCurrentCatalog(stored);
  const groups = new Set(groupIds);
  const levelIds = new Set(config.grants.filter((g) => groups.has(g.groupId)).map((g) => g.levelId));
  const levels = config.levels.filter((l) => levelIds.has(l.id));
  const capabilities = withDerived(levels.flatMap((l) => l.capabilities));
  const scope = SCOPES.find((s) => levels.some((l) => l.scope === s)) ?? null;
  return { levels: levels.map(({ id, name }) => ({ id, name })), capabilities, scope };
}

export const can = (access: EffectiveAccess, capability: Capability): boolean => access.capabilities.has(capability);

/**
 * Problems that make a configuration unsafe to save (SEC15's first half: some group must still
 * hold access.admin). Whether that group has members is checked against Entra by the API.
 */
export function validateAccessConfig(config: AccessConfig): string[] {
  const errors: string[] = [];
  const known = new Set<string>(CAPABILITIES);
  const ids = new Set<string>();
  const names = new Set<string>();
  for (const level of config.levels) {
    if (typeof level.id !== "string" || !/^[a-z0-9-]{1,40}$/.test(level.id)) errors.push("Every level needs an id.");
    if (ids.has(level.id)) errors.push(`Two levels share the id "${level.id}".`);
    ids.add(level.id);
    const name = typeof level.name === "string" ? level.name.trim() : "";
    if (!name || name.length > 40) errors.push("Every level needs a name of up to 40 characters.");
    else if (names.has(name.toLowerCase())) errors.push(`Two levels are called "${name}".`);
    else names.add(name.toLowerCase());
    if (!(SCOPES as readonly string[]).includes(level.scope)) errors.push(`Level "${level.name}" has an unknown scope.`);
    for (const c of level.capabilities) if (!known.has(c)) errors.push(`Level "${level.name}" has an unknown capability "${c}".`);
  }
  const pairs = new Set<string>();
  for (const grant of config.grants) {
    if (typeof grant.groupId !== "string" || !grant.groupId.trim() || grant.groupId.length > 64) errors.push("Every mapping needs a group's object id.");
    if (!ids.has(grant.levelId)) errors.push(`Group ${grant.groupId} is mapped to a level that does not exist.`);
    if (pairs.has(`${grant.groupId}|${grant.levelId}`)) errors.push(`Group ${grant.groupId} is mapped to the same level twice.`);
    pairs.add(`${grant.groupId}|${grant.levelId}`);
    if (grant.label !== undefined && (typeof grant.label !== "string" || grant.label.length > 80)) errors.push("A group's label is up to 80 characters.");
  }
  const adminLevels = new Set(config.levels.filter((l) => l.capabilities.includes("access.admin")).map((l) => l.id));
  if (!config.grants.some((g) => adminLevels.has(g.levelId))) {
    errors.push("No group would hold access.admin. Nobody could change access again.");
  }
  return errors;
}

/** SEC15's second half: granting pay visibility or access administration asks twice. */
export function needsSecondConfirmation(before: AccessConfig, after: AccessConfig): boolean {
  const holders = (config: AccessConfig, capability: Capability) => {
    const levels = new Set(config.levels.filter((l) => l.capabilities.includes(capability)).map((l) => l.id));
    return new Set(config.grants.filter((g) => levels.has(g.levelId)).map((g) => g.groupId));
  };
  for (const capability of ["comp.read", "access.admin"] as const) {
    const was = holders(before, capability);
    for (const groupId of holders(after, capability)) if (!was.has(groupId)) return true;
  }
  return false;
}
